import React from "react";
import { Box, Typography } from "../ui";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../shared/state/store";
import { DashboardService } from "../shared/api/services/dashboardService";
import { ActionType } from "../shared/api/types";
import {
  Home as HomeIcon,
  InsertChart as InsertChartIcon,
  Description as DescriptionIcon,
  Share as ShareIcon,
} from "@mui/icons-material";

interface TabItem {
  key: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  disabled?: boolean;
}

const BottomTabBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openBankingOnly } = useAuth();

  // Open Banking-only lock: hide bottom navigation completely
  if (openBankingOnly) {
    return null;
  }

  const tabs: TabItem[] = [
    {
      key: "dashboard",
      label: "Home",
      path: "/dashboard",
      icon: <HomeIcon sx={{ fontSize: 24 }} />,
    },
    {
      key: "budget",
      label: "Budget",
      path: "/budget",
      icon: <InsertChartIcon sx={{ fontSize: 24 }} />,
    },
    {
      key: "documents",
      label: "Documents",
      path: "/documents",
      icon: <DescriptionIcon sx={{ fontSize: 24 }} />,
    },
    {
      key: "share",
      label: "Share",
      path: "/share",
      icon: <ShareIcon sx={{ fontSize: 24 }} />,
    },
  ];

  const handleTabClick = (tab: TabItem) => {
    if (tab.disabled) return;

    // Log tab click
    DashboardService.logButtonAction({
      actionType: ActionType.TAB_CLICK,
      actionName: `Tab: ${tab.label}`,
      pageName: "Global",
      subSection: "BottomTabBar",
    }).catch(() => {});

    const currentPath = location.pathname;

    // When leaving the update-budget page, trigger an auto-save before navigation.
    // The update-budget page listens for this event and performs save + navigate.
    if (currentPath === "/update-budget") {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("iehub:budget-before-leave", {
            detail: { targetPath: tab.path },
          })
        );
      }
      return;
    }

    if (currentPath === "/documents") {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("iehub:documents-before-leave", {
            detail: { targetPath: tab.path },
          })
        );
      }
      return;
    }

    navigate(tab.path);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <Box data-testid="components-bottom-tab-bar-container-action-1"
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: "#ffffff",
        borderTop: "1px solid #e0e0e0",
        boxShadow: "0px -2px 10px rgba(0, 0, 0, 0.1)",
        display: { xs: "flex", md: "none" },
        justifyContent: "space-around",
        alignItems: "center",
        py: 1.5,
        px: 2,
        zIndex: 1200,
        pb: "calc(12px + env(safe-area-inset-bottom))",
      }}
    >
      {tabs.map((tab) => {
        const active = isActive(tab.path);
        return (
          <Box data-testid="components-bottom-tab-bar-container-action-2"
            key={tab.key}
            onClick={() => handleTabClick(tab)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1, // 8px between icon and label (design spec)
              cursor: tab.disabled ? "not-allowed" : "pointer",
              opacity: tab.disabled ? 0.4 : 1,
              minWidth: "45px", // icon + text frame width (design spec)
            }}
          >
            <Box data-testid="components-bottom-tab-bar-container-action-3"
              sx={{
                color: tab.disabled
                  ? "#9e9e9e"
                  : active
                    ? "#155f54"
                    : "#212121",
                transition: "color 0.2s",
              }}
            >
              {tab.icon}
            </Box>
            <Typography data-testid="components-bottom-tab-bar-text-action"
              variant="caption"
              sx={{
                fontSize: "12px", // design spec (flat 12px — avoids reflow on active)
                lineHeight: "20px", // design spec
                fontWeight: active ? 600 : 400,
                color: tab.disabled
                  ? "#9e9e9e"
                  : active
                    ? "#155f54"
                    : "#212121",
                transition: "color 0.2s",
              }}
            >
              {tab.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default BottomTabBar;
